"use client"

import { useMemo } from "react"
import { View, StyleSheet } from "react-native"
import { Text, useTheme } from "react-native-paper"
import { HorizontalSection } from "../../../NodeSummary/HorizontalSection"
import type { TabComponentProps } from "../../../../types/tabs"
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis, LabelList } from "recharts"
import { useUnits } from "../../../../contexts/UnitsContext"
import convert from "convert-units"

// Define a consistent color palette for dynamic assignment
const COLOR_PALETTE = [
  "#4361ee", // Blue
  "#ef476f", // Pink/Red
  "#06d6a0", // Teal
  "#ffd60a", // Yellow
  "#8338ec", // Purple
  "#ff9e00", // Orange
  "#3a86ff", // Royal Blue
  "#fb5607", // Vermilion
  "#118ab2", // Steel Blue
  "#073b4c", // Dark Teal
]

interface ProcessedData {
  name: string
  total: number
  [key: string]: number
}

export function FoundationDetailsSection({ selectedNode, projectData }: TabComponentProps) {
  const theme = useTheme()
  // Get units context
  const { activeUnits } = useUnits()

  // Default volume unit is m3
  const defaultVolumeUnit = "m3"
  // Get the active volume unit from context or use default
  const volumeUnit = activeUnits.volume || defaultVolumeUnit

  // Custom tooltip component for better readability with unit conversion
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <View style={tooltipStyles.container}>
          <Text style={tooltipStyles.label}>{label}</Text>
          <View style={tooltipStyles.divider} />
          {payload.map((entry: any, index: number) => (
            <View key={`item-${index}`} style={tooltipStyles.item}>
              <View style={[tooltipStyles.colorBox, { backgroundColor: entry.color }]} />
              <Text style={tooltipStyles.name}>{entry.name}:</Text>
              <Text style={tooltipStyles.value}>
                {convertVolumeValue(entry.value)} {volumeUnit}
              </Text>
            </View>
          ))}
          <View style={tooltipStyles.divider} />
          <View style={tooltipStyles.totalRow}>
            <Text style={tooltipStyles.totalLabel}>Total:</Text>
            <Text style={tooltipStyles.totalValue}>
              {convertVolumeValue(payload.reduce((sum: number, entry: any) => sum + entry.value, 0))} {volumeUnit}
            </Text>
          </View>
        </View>
      )
    }
    return null
  }

  // Function to convert volume values using convert-units
  const convertVolumeValue = (value: number) => {
    try {
      const convertedValue = convert(value).from(defaultVolumeUnit).to(volumeUnit)
      return convertedValue.toFixed(2)
    } catch (error) {
      // Could not convert volume unit
      return value.toFixed(2)
    }
  }

  const { towerTypeData, towerSoilTypes, soilTypeData, soilTowerTypes } = useMemo(() => {
    if (
      !selectedNode ||
      !projectData?.foundationVolumeQuantityByTowerType?.Sections ||
      !projectData?.foundationVolumeQuantity?.Sections
    ) {
      return {
        towerTypeData: [],
        towerSoilTypes: [],
        soilTypeData: [],
        soilTowerTypes: [],
      }
    }

    // Process data for Foundation Quantity by Tower Type
    const findTowerTypeData = () => {
      const sections = projectData.foundationVolumeQuantityByTowerType.Sections
      if (selectedNode.type === "line") {
        return sections.find((section) => section.FndnWeightVolume.Name === "Line")?.FndnWeightVolume
      } else if (selectedNode.type === "section") {
        return sections.find((section) => section.FndnWeightVolume.Name === selectedNode.name)?.FndnWeightVolume
      }
      return null
    }

    const towerTypeInfo = findTowerTypeData()
    const towerData = towerTypeInfo?.FndnWeightVolume?.filter((item) => item.Type.toLowerCase() === "tower") || []

    // Collect soil types for tower type chart
    const uniqueTowerSoilTypes = new Set<string>()
    towerData.forEach((tower) => {
      tower.FndnWeightVolume?.forEach((soil) => {
        if (soil.Type.toLowerCase() === "soil") {
          uniqueTowerSoilTypes.add(soil.Name)
        }
      })
    })

    // Process tower type data
    const processedTowerData: ProcessedData[] = towerData.map((tower) => {
      const data: ProcessedData = {
        name: tower.Name,
        total: tower.Quantity || 0,
      }

      tower.FndnWeightVolume?.forEach((soil) => {
        if (soil.Type.toLowerCase() === "soil") {
          data[soil.Name] = soil.Quantity || 0
        }
      })

      return data
    })

    // Process data for Foundation Quantity by Soil Type
    const findSoilTypeData = () => {
      const sections = projectData.foundationVolumeQuantity.Sections
      if (selectedNode.type === "line") {
        return sections.find((section) => section.FndnWeightVolume.Name === "Line")?.FndnWeightVolume
      } else if (selectedNode.type === "section") {
        return sections.find((section) => section.FndnWeightVolume.Name === selectedNode.name)?.FndnWeightVolume
      }
      return null
    }

    const soilTypeInfo = findSoilTypeData()
    const soilData = soilTypeInfo?.FndnWeightVolume?.filter((item) => item.Type.toLowerCase() === "soil") || []

    // Collect tower types for soil type chart
    const uniqueSoilTowerTypes = new Set<string>()
    soilData.forEach((soil) => {
      soil.FndnWeightVolume?.forEach((tower) => {
        if (tower.Type.toLowerCase() === "tower") {
          uniqueSoilTowerTypes.add(tower.Name)
        }
      })
    })

    // Process soil type data
    const processedSoilData: ProcessedData[] = soilData.map((soil) => {
      const data: ProcessedData = {
        name: soil.Name,
        total: soil.Quantity || 0,
      }

      soil.FndnWeightVolume?.forEach((tower) => {
        if (tower.Type.toLowerCase() === "tower") {
          data[tower.Name] = tower.Quantity || 0
        }
      })

      return data
    })

    return {
      towerTypeData: processedTowerData,
      towerSoilTypes: Array.from(uniqueTowerSoilTypes),
      soilTypeData: processedSoilData,
      soilTowerTypes: Array.from(uniqueSoilTowerTypes),
    }
  }, [selectedNode, projectData])

  // Calculate totals for summary
  const totalTowerVolume = towerTypeData.reduce((sum, item) => sum + item.total, 0)
  const totalSoilVolume = soilTypeData.reduce((sum, item) => sum + item.total, 0)

  // Convert values for display
  const convertedTotalTowerVolume = convertVolumeValue(totalTowerVolume)

  const content = (
    <View style={styles.chartsContainer}>
      {/* Summary cards */}
      <View style={styles.summaryContainer}>
        <View style={[styles.summaryCard, { backgroundColor: theme.colors.elevation.level2 }]}>
          <Text style={[styles.summaryLabel, { color: theme.colors.onSurface }]}>Total Foundation Volume</Text>
          <Text style={[styles.summaryValue, { color: theme.colors.primary }]}>
            {convertedTotalTowerVolume} {volumeUnit}
          </Text>
        </View>
        <View style={[styles.summaryCard, { backgroundColor: theme.colors.elevation.level2 }]}>
          <Text style={[styles.summaryLabel, { color: theme.colors.onSurface }]}>Tower Types</Text>
          <Text style={[styles.summaryValue, { color: theme.colors.primary }]}>{towerTypeData.length}</Text>
        </View>
        <View style={[styles.summaryCard, { backgroundColor: theme.colors.elevation.level2 }]}>
          <Text style={[styles.summaryLabel, { color: theme.colors.onSurface }]}>Soil Types</Text>
          <Text style={[styles.summaryValue, { color: theme.colors.primary }]}>{soilTypeData.length}</Text>
        </View>
      </View>

      {/* Add a row container for charts */}
      <View style={styles.chartsRow}>
        <View style={styles.chartWrapper}>
          <Text style={[styles.chartTitle, { color: theme.colors.onSurface }]}>
            Foundation Quantity by Tower Type ({volumeUnit})
          </Text>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={towerTypeData}
              margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
              barSize={35}
              barGap={0}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" interval={0} angle={-45} textAnchor="end" height={70} tick={{ fontSize: 12 }} />
              <YAxis
                label={{
                  value: `Volume (${volumeUnit})`,
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle" },
                }}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => convertVolumeValue(value)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: 20 }}
                formatter={(value) => <Text style={{ fontSize: 12 }}>{value}</Text>}
              />
              {towerSoilTypes.map((soilType, index) => (
                <Bar
                  key={soilType}
                  dataKey={soilType}
                  stackId="a"
                  fill={COLOR_PALETTE[index % COLOR_PALETTE.length]}
                  name={soilType}
                >
                  {/* Only show labels on the top segment for better readability */}
                  {index === towerSoilTypes.length - 1 && (
                    <LabelList
                      dataKey="total"
                      position="top"
                      style={{ fontSize: 11, fill: theme.colors.onSurface }}
                      formatter={(value: number) => convertVolumeValue(value)}
                    />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </View>

        <View style={styles.chartWrapper}>
          <Text style={[styles.chartTitle, { color: theme.colors.onSurface }]}>
            Foundation Quantity by Soil Type ({volumeUnit})
          </Text>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={soilTypeData} margin={{ top: 20, right: 30, left: 20, bottom: 70 }} barSize={35} barGap={0}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" interval={0} angle={-45} textAnchor="end" height={70} tick={{ fontSize: 12 }} />
              <YAxis
                label={{
                  value: `Volume (${volumeUnit})`,
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle" },
                }}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => convertVolumeValue(value)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: 20 }}
                formatter={(value) => <Text style={{ fontSize: 12 }}>{value}</Text>}
              />
              {soilTowerTypes.map((towerType, index) => (
                <Bar
                  key={towerType}
                  dataKey={towerType}
                  stackId="a"
                  fill={COLOR_PALETTE[index % COLOR_PALETTE.length]}
                  name={towerType}
                >
                  {/* Only show labels on the top segment for better readability */}
                  {index === soilTowerTypes.length - 1 && (
                    <LabelList
                      dataKey="total"
                      position="top"
                      style={{ fontSize: 11, fill: theme.colors.onSurface }}
                      formatter={(value: number) => convertVolumeValue(value)}
                    />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </View>
      </View>
    </View>
  )

  return (
    <HorizontalSection
      title="Foundation Details"
      content={{
        "Foundation Distribution": {
          "Tower Type Distribution": { value: "Chart 1" },
          "Soil Type Distribution": { value: "Chart 2" },
        },
      }}
      defaultExpanded={true}
      showBorders={false}
    >
      {content}
    </HorizontalSection>
  )
}

const styles = StyleSheet.create({
  chartsContainer: {
    gap: 24,
    paddingBottom: 24,
  },
  summaryContainer: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  summaryCard: {
    flex: 1,
    minWidth: 180,
    padding: 16,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  summaryLabel: {
    fontSize: 14,
    marginBottom: 8,
  },
  summaryValue: {
    fontSize: 24,
    fontWeight: "bold",
  },
  chartWrapper: {
    flex: 1,
    minWidth: "48%",
    maxWidth: "48%",
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: "500",
    marginBottom: 16,
    textAlign: "center",
  },
  chartsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 24,
  },
})

const tooltipStyles = StyleSheet.create({
  container: {
    backgroundColor: "white",
    padding: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#ccc",
    minWidth: 180,
  },
  label: {
    fontWeight: "bold",
    marginBottom: 4,
    fontSize: 14,
  },
  item: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 2,
  },
  colorBox: {
    width: 10,
    height: 10,
    marginRight: 6,
  },
  name: {
    marginRight: 4,
    fontSize: 12,
  },
  value: {
    fontWeight: "500",
    fontSize: 12,
  },
  divider: {
    height: 1,
    backgroundColor: "#eee",
    marginVertical: 4,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 2,
  },
  totalLabel: {
    fontWeight: "bold",
    fontSize: 13,
  },
  totalValue: {
    fontWeight: "bold",
    fontSize: 13,
  },
})

